/**
 * MemorialPage — public, shareable /memorial/{slug} page for a departed
 * family member (or an existing Celebrate memorial). No login required to
 * view. Logged-in users can choose/change the card design.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { http, resolveImage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SplinkCard from "@/components/SplinkCard";
import TranslatableText from "@/components/TranslatableText";
import MemorialCardHero, { lifespanLine } from "@/components/memorials/MemorialCardHero";
import {
  Download, Loader2, Copy, Check, MessageCircle, Mail, Facebook,
  Share2, Flame, Flower, Images, Palette, TreePine, Smartphone, Send, HandHeart,
} from "lucide-react";

function personPhotoUrl(person, slug) {
  const p = person?.photo;
  if (!p) return null;
  if (p.startsWith("data:")) return p;
  if (p.startsWith("http")) {
    try {
      if (new URL(p).host === window.location.host) return p;
    } catch (_) { /* fall through */ }
    return `${http.defaults.baseURL}/memorial-pages/${slug}/portrait`;
  }
  return resolveImage(p);
}

function ShareRow({ person, url }) {
  const [copied, setCopied] = useState(false);
  const text = `In loving memory of ${person.name}${lifespanLine(person) ? ` (${lifespanLine(person)})` : ""}. Visit the memorial: ${url}`;
  const enc = encodeURIComponent(text);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error("Couldn't copy — long-press the address bar instead.");
    }
  };
  const Btn = ({ href, onClick, icon: Icon, label, testId, bg }) => (
    href ? (
      <a href={href} target="_blank" rel="noreferrer" data-testid={testId}
        className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold text-white transition-transform hover:scale-105"
        style={{ background: bg }}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </a>
    ) : (
      <button type="button" onClick={onClick} data-testid={testId}
        className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold text-white transition-transform hover:scale-105"
        style={{ background: bg }}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </button>
    )
  );
  return (
    <div className="flex flex-wrap gap-2" data-testid="memorial-share-row">
      <Btn href={`https://wa.me/?text=${enc}`} icon={MessageCircle} label="WhatsApp" testId="memorial-share-whatsapp" bg="#25D366" />
      <Btn href={`sms:?&body=${enc}`} icon={Smartphone} label="SMS" testId="memorial-share-sms" bg="#0EA5E9" />
      <Btn href={`mailto:?subject=${encodeURIComponent(`In loving memory of ${person.name}`)}&body=${enc}`} icon={Mail} label="Email" testId="memorial-share-email" bg="#6366F1" />
      <Btn href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} icon={Facebook} label="Facebook" testId="memorial-share-facebook" bg="#1877F2" />
      <Btn href={`https://twitter.com/intent/tweet?text=${enc}`} icon={Share2} label="X" testId="memorial-share-x" bg="#0F172A" />
      <Btn onClick={copy} icon={copied ? Check : Copy} label={copied ? "Copied!" : "Copy link"} testId="memorial-share-copy" bg="#B25D38" />
    </div>
  );
}

const PICKER_TABS = [
  ["all", "All"], ["traditional", "Traditional"], ["floral", "Floral"], ["regal", "Regal"],
];

function TemplatePicker({ templates, currentKey, onPick, busyKey }) {
  const [cat, setCat] = useState("all");
  const visible = cat === "all" ? templates : templates.filter((t) => t.category === cat);
  return (
    <div data-testid="memorial-template-picker">
      <div className="text-[11px] font-bold tracking-widest uppercase mb-2 flex items-center gap-1.5" style={{ color: "#8a6d3b" }}>
        <Palette className="w-3.5 h-3.5" /> Choose a design
      </div>
      <div className="flex gap-1.5 mb-2.5 flex-wrap">
        {PICKER_TABS.map(([key, label]) => (
          <button key={key} type="button" onClick={() => setCat(key)}
            data-testid={`memorial-picker-tab-${key}`}
            className="rounded-full px-3 py-1 text-[11px] font-semibold transition-colors"
            style={cat === key
              ? { background: "#C2410C", color: "#fff" }
              : { background: "rgba(194,65,12,.08)", color: "#8a6d3b" }}>
            {label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2">
        {visible.map((t) => (
          <button key={t.key} type="button" onClick={() => onPick(t.key)} title={t.description}
            data-testid={`memorial-template-${t.key}`}
            className="rounded-lg overflow-hidden border-2 transition-transform hover:scale-105 relative"
            style={{ borderColor: t.key === currentKey ? "#C2410C" : "rgba(0,0,0,.08)",
              aspectRatio: t.aspect ? `${t.aspect[0]} / ${t.aspect[1]}` : "1122 / 1402" }}>
            {t.image_url ? (
              <img src={t.image_url} alt={t.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full" style={{ background: "linear-gradient(160deg,#FFF7ED,#FAE3C0)", border: "3px double #C2410C" }} />
            )}
            {busyKey === t.key && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#C2410C" }} />
              </div>
            )}
            <div className="absolute bottom-0 inset-x-0 text-[9px] font-semibold text-center py-0.5 bg-white/85" style={{ color: "#3D2B1F" }}>
              {t.name}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function WishItem({ w }) {
  return (
    <div className="rounded-xl border p-3" style={{ borderColor: "rgba(91,74,115,.2)", background: "#F5F3FF" }} data-testid={`memorial-wish-${w.id}`}>
      <div className="text-[11px] font-semibold mb-1" style={{ color: "#5B4A73" }}>{w.occasion_label}</div>
      {w.message && <div className="text-sm leading-relaxed" style={{ color: "#3D2B1F" }}><TranslatableText text={w.message} /></div>}
      {w.audio_url && <audio controls preload="metadata" src={resolveImage(w.audio_url)} className="w-full mt-2 h-9" />}
      {w.card_url && <img src={resolveImage(w.card_url)} alt="Greeting card" className="mt-2 rounded-lg max-h-48" loading="lazy" />}
      <div className="flex items-center justify-between mt-2">
        <span className="text-[11px]" style={{ color: "#8a6d3b" }}>— {w.sender_name}</span>
        <span className="text-[11px] inline-flex items-center gap-2" style={{ color: "#8a6d3b" }}>
          {w.tributes?.diya > 0 && <span>🪔 {w.tributes.diya}</span>}
          {w.tributes?.flower > 0 && <span>🌸 {w.tributes.flower}</span>}
        </span>
      </div>
    </div>
  );
}

function CondolencesSection({ slug, condolences, setData, canModerate }) {
  const [name, setName] = useState(() => localStorage.getItem("ift_guest_name") || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) return;
    setBusy(true);
    try {
      const r = await http.post(`/memorial-pages/${slug}/condolences`, {
        name: name.trim(), message: message.trim(), website: "",
      });
      localStorage.setItem("ift_guest_name", name.trim());
      setMessage("");
      if (r.data.condolence) {
        setData((d) => ({ ...d, condolences: [r.data.condolence, ...(d.condolences || [])] }));
      }
      toast.success("Thank you — your condolence has been added.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Couldn't post your message.");
    } finally {
      setBusy(false);
    }
  };

  const hide = async (cid) => {
    try {
      await http.delete(`/memorial-pages/${slug}/condolences/${cid}`);
      setData((d) => ({ ...d, condolences: d.condolences.filter((c) => c.id !== cid) }));
      toast.success("Condolence removed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove it.");
    }
  };

  return (
    <div className="rounded-2xl border bg-white p-4 sm:p-5" style={{ borderColor: "rgba(194,65,12,.15)" }} data-testid="memorial-condolences-section">
      <div className="text-[11px] font-bold tracking-widest uppercase mb-3 flex items-center gap-1.5" style={{ color: "#8a6d3b" }}>
        <HandHeart className="w-3.5 h-3.5" /> Condolences {condolences.length > 0 && `(${condolences.length})`}
      </div>
      <form onSubmit={submit} className="mb-4 space-y-2" data-testid="memorial-condolence-form">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required
          placeholder="Your name" data-testid="memorial-condolence-name"
          className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
          style={{ borderColor: "rgba(194,65,12,.25)" }} />
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={1500} required rows={3}
          placeholder="Share a memory or a message of comfort — no sign-in needed"
          data-testid="memorial-condolence-message"
          className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 resize-y"
          style={{ borderColor: "rgba(194,65,12,.25)" }} />
        <button type="submit" disabled={busy || !name.trim() || !message.trim()}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
          style={{ background: "#5B4A73" }} data-testid="memorial-condolence-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <HandHeart className="w-4 h-4" />}
          Leave a condolence
        </button>
      </form>
      {condolences.length === 0 ? (
        <div className="text-sm text-center py-4" style={{ color: "#8a6d3b" }}>
          Be the first to leave a message of comfort for the family.
        </div>
      ) : (
        <div className="space-y-3">
          {condolences.map((c) => (
            <div key={c.id} className="rounded-xl border p-3" style={{ borderColor: "rgba(194,65,12,.15)", background: "#FFFBF5" }}
              data-testid={`memorial-condolence-${c.id}`}>
              <div className="text-sm leading-relaxed whitespace-pre-line" style={{ color: "#3D2B1F" }}>{c.message}</div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] font-semibold" style={{ color: "#8a6d3b" }}>
                  — {c.name}{c.relationship ? `, ${c.relationship}` : ""}
                </span>
                {canModerate && (
                  <button type="button" onClick={() => hide(c.id)} title="Remove this condolence"
                    className="text-[11px] underline" style={{ color: "#b91c1c" }}
                    data-testid={`memorial-condolence-hide-${c.id}`}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MemorialPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [announcing, setAnnouncing] = useState(false);
  const cardRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await http.get(`/memorial-pages/${slug}`);
      setData(r.data);
    } catch (_) {
      setNotFound(true);
    }
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const pickTemplate = async (key) => {
    if (!user) {
      toast.info("Sign in to change the memorial design.");
      return;
    }
    setBusyKey(key);
    try {
      const r = await http.patch(`/memorial-pages/${slug}`, { template_key: key });
      setData((d) => ({ ...d, page: r.data.page, template: r.data.template }));
      toast.success("Design updated for everyone who visits this page.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change the design.");
    } finally {
      setBusyKey(null);
    }
  };

  const announce = async () => {
    setAnnouncing(true);
    try {
      const r = await http.post(`/memorial-pages/${slug}/announce`);
      toast.success(`Memorial page emailed to ${r.data.recipients} family member${r.data.recipients === 1 ? "" : "s"}.`);
      setData((d) => ({ ...d, page: { ...d.page, announced_at: new Date().toISOString() } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't email the family right now.");
    } finally {
      setAnnouncing(false);
    }
  };

  const uploadPhoto = async (dataUrl) => {
    try {
      await http.post(`/memorial-pages/${slug}/photo`, { data_url: dataUrl });
      toast.success("Photo added to the memorial.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't upload the photo.");
    }
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, { useCORS: true, scale: 2, backgroundColor: null });
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `${(data.person.name || "memorial").replace(/\s+/g, "-").toLowerCase()}-memorial-card.png`;
      a.click();
      toast.success("Memorial card downloaded — ready to share on WhatsApp.");
    } catch (e) {
      toast.error("Couldn't render the card image on this device.");
    } finally {
      setDownloading(false);
    }
  };

  // Signal to crawlers when the memorial slug is dead: set a
  // noindex,follow directive + a distinct <title>/<meta description>
  // so search engines don't fold this URL into the homepage cluster.
  useEffect(() => {
    if (!notFound || typeof document === "undefined") return;
    document.title = "Memorial not found · IndianFamilyTree";
    let m = document.querySelector('meta[name="robots"][data-memorial-404="1"]');
    if (!m) {
      m = document.createElement("meta");
      m.name = "robots";
      m.setAttribute("data-memorial-404", "1");
      document.head.appendChild(m);
    }
    m.setAttribute("content", "noindex,follow");
    return () => { try { m?.remove(); } catch { /* ignore */ } };
  }, [notFound]);

  if (notFound) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="memorial-page-notfound">
        <div className="text-4xl mb-3">🕊️</div>
        <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Memorial not found</h1>
        <p className="text-sm text-gray-500">This memorial page may have been moved. Ask the family for a fresh link.</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#C2410C" }} />
      </div>
    );
  }

  const { page, person, template, templates, wishes, tribute_totals, photos, condolences = [] } = data;
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const photoUrl = personPhotoUrl(person, slug);
  const biography = page.biography || person.bio;
  const quote = page.quote;

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg,#FFFBF5 0%,#FFF7ED 100%)" }} data-testid="memorial-page">
      <Helmet>
        <title>{`In loving memory of ${person.name} — IndianFamilyTree`}</title>
        <meta name="description" content={`Memorial page for ${person.name}${lifespanLine(person) ? ` (${lifespanLine(person)})` : ""}. Share a remembrance, light a candle.`} />
        <link rel="canonical" href={pageUrl} />
        {/* Open Graph — WhatsApp / Facebook / LinkedIn share cards */}
        <meta property="og:type" content="profile" />
        <meta property="og:site_name" content="IndianFamilyTree" />
        <meta property="og:title" content={`In loving memory of ${person.name}`} />
        <meta property="og:description" content={`${lifespanLine(person) || ""} · Share a remembrance on IndianFamilyTree.`} />
        <meta property="og:url" content={pageUrl} />
        {photoUrl && <meta property="og:image" content={photoUrl} />}
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`In loving memory of ${person.name}`} />
        <meta name="twitter:description" content={`${lifespanLine(person) || ""} · Light a candle on IndianFamilyTree.`} />
        {photoUrl && <meta name="twitter:image" content={photoUrl} />}
        {/* JSON-LD — Person + BreadcrumbList so search engines
            understand this is a memorial for a specific individual,
            not a duplicate of the homepage. */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          "name": person.name,
          "birthDate": person.birth_date || undefined,
          "deathDate": person.death_date || undefined,
          "birthPlace": person.birth_place || undefined,
          "image": photoUrl || undefined,
          "description": biography || `Memorial page for ${person.name}.`,
          "url": pageUrl,
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": typeof window !== "undefined" ? window.location.origin + "/" : "/" },
            { "@type": "ListItem", "position": 2, "name": "Memorials", "item": (typeof window !== "undefined" ? window.location.origin : "") + "/memorial" },
            { "@type": "ListItem", "position": 3, "name": person.name, "item": pageUrl },
          ],
        })}</script>
      </Helmet>
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        <div className="text-center mb-8">
          <div className="text-[11px] font-bold tracking-[0.35em] uppercase mb-2" style={{ color: "#B45309" }}>
            In Loving Memory · Eternal Memorial
          </div>
          <h1 className="text-3xl sm:text-4xl" style={{ fontFamily: "'Playfair Display', serif", color: "#3D2B1F" }}>
            {person.name}
          </h1>
          {lifespanLine(person) && (
            <div className="text-sm mt-1" style={{ color: "#8a6d3b" }}>{lifespanLine(person)}</div>
          )}
          {(tribute_totals.diya > 0 || tribute_totals.flower > 0) && (
            <div className="mt-2 text-sm" style={{ color: "#5B4A73" }} data-testid="memorial-tribute-totals">
              <Flame className="w-3.5 h-3.5 inline -mt-0.5" /> {tribute_totals.diya} candles · <Flower className="w-3.5 h-3.5 inline -mt-0.5" /> {tribute_totals.flower} flowers from the family
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-[400px,1fr] gap-8 items-start">
          {/* Left — the card */}
          <div className="lg:sticky lg:top-20 space-y-4 max-w-md mx-auto w-full">
            <MemorialCardHero template={template} person={person} photoUrl={photoUrl} quote={quote} innerRef={cardRef}
              onAddPhoto={user ? uploadPhoto : null} />
            <button type="button" onClick={downloadCard} disabled={downloading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02]"
              style={{ background: "#C2410C" }} data-testid="memorial-download-btn">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download card (PNG)
            </button>
            {user ? (
              <TemplatePicker templates={templates} currentKey={page.template_key} onPick={pickTemplate} busyKey={busyKey} />
            ) : (
              <div className="text-xs text-center rounded-lg border p-2.5" style={{ borderColor: "rgba(194,65,12,.25)", color: "#8a6d3b" }} data-testid="memorial-picker-signin-hint">
                <Link to={`/login?next=/memorial/${slug}`} className="underline font-semibold" style={{ color: "#C2410C" }}>Sign in</Link> to choose a different card design.
              </div>
            )}
          </div>

          {/* Right — share, story, wishes, gallery */}
          <div className="space-y-6">
            <div className="rounded-2xl border bg-white p-4 sm:p-5" style={{ borderColor: "rgba(194,65,12,.15)" }}>
              <div className="text-[11px] font-bold tracking-widest uppercase mb-3" style={{ color: "#8a6d3b" }}>
                Share this memorial
              </div>
              <ShareRow person={person} url={pageUrl} />
              {user && page.tree_id && (
                <button type="button" onClick={announce} disabled={announcing}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold transition-colors"
                  style={{ background: "#F5F3FF", color: "#5B4A73", border: "1px solid rgba(91,74,115,.3)" }}
                  data-testid="memorial-announce-btn">
                  {announcing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Email this memorial to the whole family
                </button>
              )}
              <div className="mt-4">
                <SplinkCard url={pageUrl} label={`Memorial — ${person.name}`} headline="Scan or share the short link" testId="memorial-splink-card" />
              </div>
            </div>

            {biography && (
              <div className="rounded-2xl border bg-white p-4 sm:p-5" style={{ borderColor: "rgba(194,65,12,.15)" }} data-testid="memorial-biography">
                <div className="text-[11px] font-bold tracking-widest uppercase mb-2" style={{ color: "#8a6d3b" }}>Their story</div>
                <div className="text-sm leading-relaxed whitespace-pre-line" style={{ color: "#3D2B1F" }}>
                  <TranslatableText text={biography} />
                </div>
              </div>
            )}

            <div className="rounded-2xl border bg-white p-4 sm:p-5" style={{ borderColor: "rgba(194,65,12,.15)" }} data-testid="memorial-wishes-section">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] font-bold tracking-widest uppercase" style={{ color: "#8a6d3b" }}>
                  Remembrances from the family {wishes.length > 0 && `(${wishes.length})`}
                </div>
                {person.tree_slug && (
                  <Link to={`/family/${person.tree_slug}/wishes?member=${page.member_id}`}
                    className="text-xs font-semibold underline" style={{ color: "#C2410C" }} data-testid="memorial-add-remembrance-link">
                    Family member? Add a remembrance →
                  </Link>
                )}
              </div>
              {wishes.length === 0 ? (
                <div className="text-sm text-center py-6" style={{ color: "#8a6d3b" }}>
                  🪔 No remembrances shared yet. Family members can light a candle and share a memory.
                </div>
              ) : (
                <div className="space-y-3">{wishes.map((w) => <WishItem key={w.id} w={w} />)}</div>
              )}
            </div>

            <CondolencesSection slug={slug} condolences={condolences} setData={setData} canModerate={!!user} />

            {photos.length > 0 && (
              <div className="rounded-2xl border bg-white p-4 sm:p-5" style={{ borderColor: "rgba(194,65,12,.15)" }} data-testid="memorial-gallery">
                <div className="text-[11px] font-bold tracking-widest uppercase mb-3 flex items-center gap-1.5" style={{ color: "#8a6d3b" }}>
                  <Images className="w-3.5 h-3.5" /> Photo memories ({photos.length})
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {photos.map((p, i) => (
                    <img key={i} src={resolveImage(p.url)} alt={p.caption || person.name}
                      className="w-full aspect-square object-cover rounded-lg" loading="lazy" />
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-dashed p-4 sm:p-5 text-center" style={{ borderColor: "rgba(91,74,115,.35)", background: "#FAF8FF" }} data-testid="memorial-chat-placeholder">
              <MessageCircle className="w-5 h-5 mx-auto mb-1.5" style={{ color: "#5B4A73" }} />
              <div className="text-sm font-semibold" style={{ color: "#5B4A73" }}>Family Chat — coming soon</div>
              <div className="text-xs mt-0.5" style={{ color: "#8a6d3b" }}>
                A private condolence chat powered by SocialPulse Chats is on its way.
              </div>
            </div>

            {person.tree_slug && (
              <Link to={`/family/${person.tree_slug}`} className="inline-flex items-center gap-1.5 text-sm font-semibold underline" style={{ color: "#C2410C" }} data-testid="memorial-tree-link">
                <TreePine className="w-4 h-4" /> Visit the {person.tree_name || "family"} tree
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
